import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from '@/components/primitives';
import { stageLabel } from '@/i18n/labels';
import type { Country, CountryCode, FlagPalette, Match, Venue } from '@/types/domain';
import { formatKickoffDate, formatKickoffTime } from '@/utils/time';

// Our brand wordmark — a proper noun, never localized.
const BRAND_WORDMARK = 'nynjwc';

interface ShareCardProps {
  match: Match;
  venue: Venue;
  venueCountry: Country;
  /** Resolved Country for match.home — undefined for unresolved knockout slots. */
  homeCountry: Country | undefined;
  /** Resolved Country for match.away — undefined for unresolved knockout slots. */
  awayCountry: Country | undefined;
  /** Optional hero image for the home side. Layered on top of the gradient
   * fallback; when absent the gradient shows through. Same-origin only —
   * cross-origin URLs will silently fail html-to-image capture. */
  homeImageSrc?: string;
  /** Optional hero image for the away side. Same constraints as homeImageSrc. */
  awayImageSrc?: string;
}

// 1080×1920 = IG Stories canvas. Top/bottom 250px get covered by IG chrome
// (avatar + reply UI). All load-bearing content lives in the middle 1420px.
// The bottom safe zone still gets a minimal mark since screenshots/downloads
// preserve it.
const W = 1080;
const H = 1920;

const MATCHUP_BOTTOM = 1100;
const BRAND_BOTTOM_OFFSET = 150;

const MONO: CSSProperties = { fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' };
const SERIF: CSSProperties = { fontFamily: '"Instrument Serif", Georgia, serif' };

const COUNTRY_CODE_SHADOW = '0 4px 24px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.4)';
const VS_SHADOW = '0 0 32px rgba(0,0,0,0.85), 0 6px 20px rgba(0,0,0,0.7)';
const STAGE_SHADOW = '0 2px 10px rgba(0,0,0,0.85)';

export function ShareCard({
  match,
  venue,
  venueCountry,
  homeCountry,
  awayCountry,
  homeImageSrc,
  awayImageSrc,
}: ShareCardProps) {
  const { t } = useTranslation();
  // Unresolved knockout slots (e.g. "Winner Group A") have no Country lookup.
  // Fall back to the venue's country palette so the layout stays balanced.
  const homePalette = (homeCountry ?? venueCountry).colors;
  const awayPalette = (awayCountry ?? venueCountry).colors;

  // Prefer the 3-letter FIFA code; fall back to the bracket label.
  const homeLabel = match.home ?? match.homeLabel ?? t('tbd');
  const awayLabel = match.away ?? match.awayLabel ?? t('tbd');

  return (
    <div
      style={{
        width: W,
        height: H,
        background: '#0a0a0a',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '-apple-system, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: MATCHUP_BOTTOM,
          display: 'flex',
        }}
      >
        <SideField label={homeLabel} palette={homePalette} side="left" imageSrc={homeImageSrc} code={match.home ?? undefined} />
        <SideField label={awayLabel} palette={awayPalette} side="right" imageSrc={awayImageSrc} code={match.away ?? undefined} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: MATCHUP_BOTTOM / 2,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          width: 460,
        }}
      >
        <div
          style={{
            ...SERIF,
            fontStyle: 'italic',
            fontSize: 220,
            fontWeight: 400,
            color: '#fff',
            textShadow: VS_SHADOW,
            lineHeight: 1,
            letterSpacing: -8,
          }}
        >
          {t('vs')}
        </div>
        {match.stage && (
          <div
            style={{
              ...MONO,
              fontSize: 26,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: 4,
              textTransform: 'uppercase',
              marginTop: 14,
              textShadow: STAGE_SHADOW,
            }}
          >
            {stageLabel(match.stage, t)}
          </div>
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          top: MATCHUP_BOTTOM,
          left: 0,
          right: 0,
          bottom: 0,
          background: venueCountry.colors.primary,
          opacity: 0.12,
          pointerEvents: 'none',
        }}
        aria-hidden
      />

      <div
        style={{
          position: 'absolute',
          top: MATCHUP_BOTTOM,
          left: 0,
          right: 0,
          height: 12,
          display: 'flex',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <div style={{ flex: 1, background: venueCountry.colors.primary }} />
        <div style={{ flex: 1, background: venueCountry.colors.secondary }} />
        <div style={{ flex: 1, background: venueCountry.colors.tertiary }} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: MATCHUP_BOTTOM,
          left: 0,
          right: 0,
          padding: '62px 80px 0',
        }}
      >
        <div
          style={{
            ...MONO,
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: 7,
            color: accentForDark(venueCountry.colors.primary),
            textTransform: 'uppercase',
          }}
        >
          {t('imGoingTo')}
        </div>
        <div
          style={{
            ...SERIF,
            fontStyle: 'italic',
            fontSize: 120,
            lineHeight: 1.02,
            color: '#fff',
            marginTop: 14,
            letterSpacing: -2,
          }}
        >
          {venue.name}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 18,
            marginTop: 22,
          }}
        >
          <div
            style={{
              ...MONO,
              fontSize: 56,
              fontWeight: 900,
              letterSpacing: 4,
              color: accentForDark(venueCountry.colors.primary),
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {venue.hood}
          </div>
        </div>
        <div
          style={{
            ...MONO,
            fontSize: 38,
            fontWeight: 700,
            letterSpacing: 3,
            color: '#fff',
            textTransform: 'uppercase',
            marginTop: 44,
          }}
        >
          {formatKickoffDate(match.kickoffAt)} · {formatKickoffTime(match.kickoffAt)}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: BRAND_BOTTOM_OFFSET,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            width: 140,
            height: 5,
            marginBottom: 26,
            borderRadius: 3,
            overflow: 'hidden',
          }}
          aria-hidden
        >
          <div style={{ flex: 1, background: venueCountry.colors.primary }} />
          <div style={{ flex: 1, background: venueCountry.colors.secondary }} />
          <div style={{ flex: 1, background: venueCountry.colors.tertiary }} />
        </div>
        <div
          style={{
            ...MONO,
            fontSize: 42,
            fontWeight: 900,
            color: '#fff',
            letterSpacing: 12,
            textTransform: 'uppercase',
          }}
        >
          {BRAND_WORDMARK}
        </div>
        <div
          style={{
            ...SERIF,
            fontStyle: 'italic',
            fontSize: 30,
            color: '#9a9590',
            marginTop: 10,
            letterSpacing: 0.5,
          }}
        >
          {t('findYourSpot')}
        </div>
      </div>
    </div>
  );
}

// WCAG-style relative luminance. Used to pick a readable text color over
// the country's primary — countries with light primaries (yellow flags like
// Colombia, Ecuador) need dark text; everyone else gets white.
function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const toLin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function textOver(bg: string): { color: string; shadow: string } {
  const isLight = relativeLuminance(bg) > 0.55;
  return isLight
    ? { color: '#0a0a0a', shadow: '0 2px 10px rgba(255,255,255,0.45)' }
    : { color: '#ffffff', shadow: COUNTRY_CODE_SHADOW };
}

// Lighten a hex toward white. Used to make a country's primary color usable
// as text on a dark background — raw navy (#3C3B6E) reads as a low-contrast
// blur, the lightened version (#8A89A8) reads clean while staying on-hue.
function lighten(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${toHex(lr)}${toHex(lg)}${toHex(lb)}`;
}

// Returns a version of `primary` that's readable as text on a dark bg.
// Very dark primaries get heavier lightening so they don't disappear.
function accentForDark(primary: string): string {
  const L = relativeLuminance(primary);
  if (L < 0.08) return lighten(primary, 0.65);  // near-black primaries (Germany)
  if (L < 0.2) return lighten(primary, 0.45);   // dark primaries (USA navy, deep reds)
  return lighten(primary, 0.25);                // already-bright primaries get a small bump
}

function SideField({
  label,
  palette,
  side,
  imageSrc,
  code,
}: {
  label: string;
  palette: FlagPalette;
  side: 'left' | 'right';
  imageSrc: string | undefined;
  code: CountryCode | undefined;
}) {
  const text = textOver(palette.primary);
  // Smooth 3-color gradient using the country's full flag palette. Mirrored
  // angles per side — bright primary at the outer-top, fading through
  // secondary to tertiary at the seam-bottom.
  const angle = side === 'left' ? 135 : 225;
  const gradient = `linear-gradient(${angle}deg, ${palette.primary} 0%, ${palette.secondary} 55%, ${palette.tertiary} 100%)`;
  const background = imageSrc
    ? `url(${JSON.stringify(imageSrc)}) center/cover no-repeat, ${gradient}`
    : gradient;

  // Flag chip sits in the outer-top corner of each half — opposite the VS
  // seam so it doesn't fight with the centered country code text.
  const chipPosition: CSSProperties = side === 'left'
    ? { top: 50, left: 50 }
    : { top: 50, right: 50 };

  return (
    <div
      style={{
        flex: 1,
        background,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
      }}
    >
      {code && (
        <div
          style={{
            position: 'absolute',
            ...chipPosition,
            zIndex: 2,
            filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.35))',
          }}
          aria-hidden
        >
          <Flag code={code} w={120} h={80} radius={8} />
        </div>
      )}
      <span
        style={{
          fontSize: 200,
          fontWeight: 900,
          color: text.color,
          letterSpacing: -6,
          textShadow: text.shadow,
          lineHeight: 1,
        }}
      >
        {label}
      </span>
    </div>
  );
}
